/* @flow */
import cx from 'classnames';
import invariant from 'invariant';
import * as React from 'react';
import ReactDOM from 'react-dom';

import createChainedFunction from 'utils/createChainedFunction';

import './styles/Text.scss';

// # TODO
// * Enforce `focus`-able element with `on*` props

export type TextAlign = 'inherit' | 'left' | 'center' | 'right';

export type TextSize =
  | 'xxxxlarge'
  | 'xxxlarge'
  | 'xxlarge'
  | 'xlarge'
  | 'large'
  | 'medium'
  | 'small'
  | 'xsmall'
  | 'xxsmall'
  | 'xxxsmall'
  | 'xxxxsmall'
  | 'xxxxxsmall';

export type Props = {
  align?: TextAlign,
  capitalize?: boolean,
  children: any,
  className?: string,
  color?:
    | 'inherit'
    | 'lava'
    | 'flame'
    | 'sunrise'
    | 'jungle'
    | 'shamrock'
    | 'midnight'
    | 'brand'
    | 'violet'
    | 'pitch'
    | 'thunder'
    | 'eclipse'
    | 'koala'
    | 'wolf'
    | 'white',
  decoration?: 'dashes',
  element: 'h1' | 'h2' | 'h3' | 'h4' | 'div' | 'section' | 'p' | 'span' | 'code',
  italic?: boolean,
  lineHeight?: 'xlarge' | 'large' | 'medium',
  size?: TextSize,
  smallCaps?: boolean,
  truncate?: boolean,
  uppercase?: boolean,
  weight?: 'bold' | 'semibold' | 'normal' | 'light',
  wrap?: boolean,
  onBlur?: Function,
  onClick?: Function,
  onFocus?: Function,
  onMouseEnter?: Function,
  onMouseLeave?: Function,
  onMouseOver?: Function,
  'data-test-selector'?: string,
};

type State = {
  label: string,
};

export default class Text extends React.Component<Props, State> {
  static defaultProps = {
    align: 'inherit',
    element: 'span',
    wrap: true,
  };

  state = {
    label: '',
  };

  handleMouseOver = () => {
    if (!this.props.truncate) {
      return;
    }

    const container = ReactDOM.findDOMNode(this);
    invariant(container instanceof window.HTMLElement, 'Text findDOMNode returned non HTMLElement');

    const textWidth = container.childNodes[0].getBoundingClientRect().width;
    const visibleWidth = container.getBoundingClientRect().width;

    if (textWidth > visibleWidth) {
      this.setState({ label: container.textContent });
    } else {
      this.setState({ label: '' });
    }
  };

  render() {
    const {
      align,
      children,
      capitalize,
      className,
      color,
      decoration,
      element,
      italic,
      lineHeight,
      onClick,
      onMouseOver,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      size,
      smallCaps,
      truncate,
      uppercase,
      weight,
      wrap,
    } = this.props;
    const { label } = this.state;

    const Component = element;

    const body = truncate ? <span>{children}</span> : children;

    return (
      <Component
        title={label}
        className={cx(
          'Text',
          align && `Text--${align}`,
          capitalize && 'Text--capitalize',
          color && `Text--${color}`,
          decoration && `Text--${decoration}`,
          italic && 'Text--italic',
          lineHeight && `Text--line-height--${lineHeight}`,
          size && `Text--${size}`,
          smallCaps && 'Text--smallCaps',
          truncate && 'Text--truncate',
          uppercase && 'Text--uppercase',
          weight && `Text--${weight}`,
          !wrap && 'Text--noWrap',
          className
        )}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseOver={createChainedFunction(this.handleMouseOver, onMouseOver)}
        data-test-selector={this.props['data-test-selector']}>
        {body}
      </Component>
    );
  }
}
